import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CreateDiscountOptions,
  Discount,
  DiscountCreatedEnvelope,
  DiscountUpdatedEnvelope,
  GetDiscountIdentifiers,
  ListDiscountsOptions,
  ListDiscountsResponse,
  UpdateDiscountIdentifiers,
  UpdateDiscountOptions,
  createDiscount as universalCreateDiscount,
  getDiscount as universalGetDiscount,
  listDiscounts as universalListDiscounts,
  updateDiscount as universalUpdateDiscount,
} from './restaurants-catalogs-v3-discount-discount.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function getDiscount(httpClient: HttpClient): GetDiscountSignature {
  return (
    identifiers: NonNullablePaths<
      GetDiscountIdentifiers,
      `catalogId` | `discountId`,
      2
    >
  ) =>
    universalGetDiscount(
      identifiers,
      // @ts-ignore
      { httpClient }
    );
}

interface GetDiscountSignature {
  /**
   * Retrieves a discount.
   * @returns Retrieved discount.
   * @deprecated
   */
  (
    identifiers: NonNullablePaths<
      GetDiscountIdentifiers,
      `catalogId` | `discountId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      Discount,
      | `amount.value`
      | `amount.currency`
      | `sectionIds.values`
      | `itemIds.values`
      | `type`
      | `condition.fulfillmentTypes`
      | `condition.platforms`
      | `condition.availability.periods`
      | `condition.availability.periods.${number}.openDay`
      | `condition.availability.periods.${number}.openTime`
      | `condition.availability.periods.${number}.closeDay`
      | `condition.availability.periods.${number}.closeTime`
      | `condition.availability.specialHourPeriods`
      | `condition.availability.specialHourPeriods.${number}.startDate`
      | `condition.availability.specialHourPeriods.${number}.endDate`
      | `condition.availability.specialHourPeriods.${number}.available`,
      6
    >
  >;
}

export function updateDiscount(
  httpClient: HttpClient
): UpdateDiscountSignature {
  return (
    identifiers: NonNullablePaths<UpdateDiscountIdentifiers, `catalogId`, 2>,
    options: NonNullablePaths<UpdateDiscountOptions, `discount`, 2>
  ) =>
    universalUpdateDiscount(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateDiscountSignature {
  /**
   * Updates a discount.
   * A discount can belong to a catalog, section, or dish. You can't create a discount that applies to a menu.
   * @returns Updated discount.
   * @deprecated
   */
  (
    identifiers: NonNullablePaths<UpdateDiscountIdentifiers, `catalogId`, 2>,
    options: NonNullablePaths<UpdateDiscountOptions, `discount`, 2>
  ): Promise<
    NonNullablePaths<
      Discount,
      | `amount.value`
      | `amount.currency`
      | `sectionIds.values`
      | `itemIds.values`
      | `type`
      | `condition.fulfillmentTypes`
      | `condition.platforms`
      | `condition.availability.periods`
      | `condition.availability.periods.${number}.openDay`
      | `condition.availability.periods.${number}.openTime`
      | `condition.availability.periods.${number}.closeDay`
      | `condition.availability.periods.${number}.closeTime`
      | `condition.availability.specialHourPeriods`
      | `condition.availability.specialHourPeriods.${number}.startDate`
      | `condition.availability.specialHourPeriods.${number}.endDate`
      | `condition.availability.specialHourPeriods.${number}.available`,
      6
    >
  >;
}

export function createDiscount(
  httpClient: HttpClient
): CreateDiscountSignature {
  return (
    catalogId: string,
    options?: NonNullablePaths<
      CreateDiscountOptions,
      | `discount.active`
      | `discount.condition.fulfillmentTypes`
      | `discount.condition.minOrderPrice`
      | `discount.condition.platforms`
      | `discount.name`,
      4
    >
  ) =>
    universalCreateDiscount(
      catalogId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateDiscountSignature {
  /**
   * Creates a discount.
   * You can create discounts for catalogs, sections, or dishes. You can't create a discount that applies to a menu.
   * @param - ID of the catalog the discount belongs to.
   * @returns Created discount.
   * @deprecated
   */
  (
    catalogId: string,
    options?: NonNullablePaths<
      CreateDiscountOptions,
      | `discount.active`
      | `discount.condition.fulfillmentTypes`
      | `discount.condition.minOrderPrice`
      | `discount.condition.platforms`
      | `discount.name`,
      4
    >
  ): Promise<
    NonNullablePaths<
      Discount,
      | `amount.value`
      | `amount.currency`
      | `sectionIds.values`
      | `itemIds.values`
      | `type`
      | `condition.fulfillmentTypes`
      | `condition.platforms`
      | `condition.availability.periods`
      | `condition.availability.periods.${number}.openDay`
      | `condition.availability.periods.${number}.openTime`
      | `condition.availability.periods.${number}.closeDay`
      | `condition.availability.periods.${number}.closeTime`
      | `condition.availability.specialHourPeriods`
      | `condition.availability.specialHourPeriods.${number}.startDate`
      | `condition.availability.specialHourPeriods.${number}.endDate`
      | `condition.availability.specialHourPeriods.${number}.available`,
      6
    >
  >;
}

export function listDiscounts(httpClient: HttpClient): ListDiscountsSignature {
  return (catalogId: string, options?: ListDiscountsOptions) =>
    universalListDiscounts(
      catalogId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListDiscountsSignature {
  /**
   * Retrieves up to 1000 discounts.
   * @param - ID of the catalog the discounts belong to.
   * @deprecated
   */
  (catalogId: string, options?: ListDiscountsOptions): Promise<
    NonNullablePaths<
      ListDiscountsResponse,
      | `discounts`
      | `discounts.${number}.amount.value`
      | `discounts.${number}.amount.currency`
      | `discounts.${number}.type`,
      5
    >
  >;
}

export const onDiscountCreated = EventDefinition(
  'wix.restaurants.catalogs.v3.discount_created',
  true,
  (event: DiscountCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<DiscountCreatedEnvelope>();
export const onDiscountUpdated = EventDefinition(
  'wix.restaurants.catalogs.v3.discount_updated',
  true,
  (event: DiscountUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<DiscountUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AddLoyaltyDiscount,
  Availability,
  BaseEventMetadata,
  Coupon,
  CreateDiscountOptions,
  CreateDiscountRequest,
  CreateDiscountResponse,
  DayOfWeek,
  Discount,
  DiscountApplyToFilterOneOf,
  DiscountCondition,
  DiscountCreatedEnvelope,
  DiscountPlatform,
  DiscountType,
  DiscountUpdatedEnvelope,
  DiscountValueOneOf,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  FulfillmentType,
  GetDiscountIdentifiers,
  GetDiscountRequest,
  GetDiscountResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemIds,
  ListDiscountsOptions,
  ListDiscountsRequest,
  ListDiscountsResponse,
  MessageEnvelope,
  Money,
  RestoreInfo,
  SectionIds,
  SpecialHourPeriod,
  TimePeriod,
  UpdateDiscountIdentifiers,
  UpdateDiscountOptions,
  UpdateDiscountRequest,
  UpdateDiscountResponse,
  WebhookIdentityType,
} from './restaurants-catalogs-v3-discount-discount.universal.js';
